import classnames from 'classnames';
import { type FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { learnMore } from '../../messages/pdpBioMessages';
import { dibsLazyClient } from 'dibs-lazy/src/dibsLazyClient';
import { getIsNewSeller } from '../../../utils/sellerHelpers';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { useRespUnavailableContext } from '../../respUnavailable/PdpRespUnavailableContext/PdpRespUnavailableContext';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { ClientSuspense } from 'dibs-elements/src/ClientSuspense';
import { PdpSharedSellerDetailsFeedback } from './PdpSharedSellerDetailsFeedback';
import { SharedStarRating } from '../../../shared/SharedStarRating/SharedStarRating';
import { PdpSharedNewSellerPill } from '../PdpSharedNewSellerPill/PdpSharedNewSellerPill';

// styles
import styles from './PdpSharedSellerDetailsHeader.scss';

// types
import {
    type PdpSharedSellerDetailsHeader_seller$key as Seller,
    type PdpSharedSellerDetailsHeader_seller$data as SellerData,
} from './__generated__/PdpSharedSellerDetailsHeader_seller.graphql';
import { type PdpSharedSellerDetailsHeader_user$key as User } from './__generated__/PdpSharedSellerDetailsHeader_user.graphql';

// lazy load
const PdpSharedSellerDetailsBioModal = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedSellerDetailsBioModal" */ './PdpSharedSellerDetailsBioModal'
        )
);

const shouldShowBioModal = (seller: SellerData): boolean => {
    let hasVideo = false;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const mediaList =
        seller?.liveStorefrontProfile?.galleryAlbum?.mediaList?.filter(media => {
            const element = media?.element;
            const { url, path } = element || {};
            if (url) {
                hasVideo = true;
            }
            return path || url;
        }) || [];

    return !isRewardsTier2 && (hasVideo || mediaList.length > 1);
};

const userFragment = graphql`
    fragment PdpSharedSellerDetailsHeader_user on User {
        canShowSellerName
    }
`;

const sellerFragment = graphql`
    fragment PdpSharedSellerDetailsHeader_seller on Seller
    @argumentDefinitions(isExtended: { type: "Boolean", defaultValue: false }) {
        ...SharedStarRating_seller
        ...PdpSharedSellerDetailsFeedback_seller
        ...PdpSharedSellerDetailsBioModal_seller @arguments(isExtended: $isExtended)
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        sellerProfile {
            company
        }
        shippingAddress {
            displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
        }
        liveStorefrontProfile {
            headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
            galleryAlbum {
                mediaList {
                    element {
                        ... on Photo {
                            path
                        }
                        ... on Video {
                            url
                        }
                    }
                }
            }
        }
    }
`;

export const PdpSharedSellerDetailsHeader: FC<{
    seller: Seller;
    user: User | null | undefined;
    isMobile?: boolean;
    hideTitle?: boolean;
    isCompact?: boolean;
    inlineHeader?: boolean;
    inlineBioLink?: boolean;
}> = ({
    user: userRef,
    isMobile = false,
    isCompact = false,
    seller: sellerRef,
    inlineHeader = true,
    inlineBioLink = false,
}) => {
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const {
        relayVars: { afterDisplay },
    } = useRespUnavailableContext();

    const { isRewardsTier2, shippingAddress, monthsSinceStartDate, liveStorefrontProfile } =
        seller || {};
    const canShowSellerName = afterDisplay && user?.canShowSellerName;
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const dealerLocation = shippingAddress?.displayFullCityStateCountry;
    const { headshots, galleryAlbum } = liveStorefrontProfile || {};
    const { status: headshotStatus, path: headshotPath } = headshots?.[0] || {};
    const showHeadshot = isRewardsTier2 && headshotStatus === 'ACTIVE' && !!headshotPath;
    const mediaList = galleryAlbum?.mediaList || [];

    const [isBioModalOpen, setIsBioModalOpen] = useState(false);

    let sellerImagePath;

    if (showHeadshot) {
        sellerImagePath = headshotPath;
    } else if (mediaList.length) {
        sellerImagePath = mediaList.find(media => media?.element?.path)?.element?.path;
    }

    const detailsHeader = (
        <div
            data-tn="pdp-shared-dealer-details-header"
            className={classnames(styles.headerPrimary, { [styles.small]: isCompact })}
        >
            <span
                className={classnames(styles.sellerTitle, {
                    [styles.spaceRight]: isNewSeller,
                })}
            >
                {canShowSellerName ? (
                    seller?.sellerProfile?.company
                ) : (
                    <FormattedMessage
                        defaultMessage="About the Seller"
                        id="PdpSharedSellerDetailsHeader.seller_title"
                    />
                )}
            </span>
            {isNewSeller && (
                <span className={classnames(styles.pill, { [styles.align]: !isCompact })}>
                    <PdpSharedNewSellerPill />
                </span>
            )}
        </div>
    );
    const sellerImage = sellerImagePath ? (
        <img
            alt="" // decorative image
            src={sellerImagePath}
            className={styles.image}
            sizes="(max-width:768px) 60px, 80px, 100px"
            srcSet={getSrcsetString(sellerImagePath, [100, 200, 300])}
        />
    ) : null;

    const openBioModal = useCallback(() => setIsBioModalOpen(true), []);
    const closeBioModal = useCallback(() => setIsBioModalOpen(false), []);

    return (
        <>
            {!inlineHeader && <div className={styles.inlineHeader}>{detailsHeader}</div>}
            <div className={styles.container}>
                {sellerImage &&
                    ((shouldShowBioModal(seller) && !inlineBioLink) || isCompact ? (
                        <Link
                            onClick={openBioModal}
                            className={classnames(styles.imageContainer, {
                                [styles.compact]: isCompact,
                            })}
                        >
                            {sellerImage}
                        </Link>
                    ) : (
                        <div
                            className={classnames(styles.imageContainer, {
                                [styles.compact]: isCompact,
                            })}
                        >
                            {sellerImage}
                        </div>
                    ))}
                <div className={styles.header}>
                    {inlineHeader && detailsHeader}
                    <div className={isCompact ? styles.reviewsCompact : styles.reviews}>
                        <div className={styles.reviewsStarRating}>
                            <SharedStarRating
                                size={isCompact ? 'medium' : 'small'}
                                seller={seller}
                            />
                        </div>
                        <PdpSharedSellerDetailsFeedback seller={seller} isMobile={isMobile} />
                    </div>
                    <span className={styles.location} data-tn="dealer-location">
                        <FormattedMessage
                            id="PdpSharedSellerDetailsHeader.location"
                            defaultMessage="Located in {dealerLocation}"
                            values={{ dealerLocation }}
                        />
                    </span>
                    {(shouldShowBioModal(seller) || isCompact) && (
                        <ClientSuspense fallback={null}>
                            {inlineBioLink && (
                                <Link className={styles.learnMore} onClick={openBioModal}>
                                    {learnMore}
                                </Link>
                            )}
                            <PdpSharedSellerDetailsBioModal
                                seller={seller}
                                isExtended={isCompact}
                                isOpen={isBioModalOpen}
                                onClose={closeBioModal}
                            />
                        </ClientSuspense>
                    )}
                </div>
            </div>
        </>
    );
};
